import { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react'
import { LayoutGroup } from 'framer-motion'

/**
 * Sprint 8 — Fluid Interface
 * No hay páginas en aurIA. Hay un espacio continuo donde los componentes existen
 * en distintos estados de expansión y se transforman entre ellos.
 *
 * Estados: micro | compact | expanded | full
 * Regla: solo un componente puede estar en `full` a la vez.
 *
 * Ley: "Nothing disappears. Everything transforms."
 */

const FluidContext = createContext(null)

const INITIAL_STATES = {
  session_card: 'compact',
  nutrition_panel: 'compact',
  auria_chat: 'compact',
  mobility_guide: 'compact',
  progress_chain: 'compact',
  week_view: 'micro'
}

const NEXT_STATE = { micro: 'compact', compact: 'expanded', expanded: 'full' }
const PREV_STATE = { full: 'expanded', expanded: 'compact', compact: 'micro' }

export function FluidProvider({ children }) {
  const [componentStates, setComponentStates] = useState(INITIAL_STATES)
  // El contexto actual — qué está mirando el usuario en este momento.
  // Lo consume AuriaCommandOverlay para que aurIA "sepa" sin que el usuario explique.
  const [currentContext, setCurrentContext] = useState(null)

  // Sprint 8.3 H6 — snapshot del estado previo antes de entrar en FULL para
  // poder restaurarlo limpiamente al colapsar. Antes los componentes secundarios
  // quedaban en micro tras volver de un fullscreen.
  const previousStatesRef = useRef(null)

  const setComponentState = useCallback((componentId, state) => {
    setComponentStates(prev => {
      if (state === 'full') {
        previousStatesRef.current = { ...prev }
        const next = {}
        for (const k of Object.keys(prev)) {
          next[k] = k === componentId ? 'full' : 'micro'
        }
        return next
      }
      // Restaurar al salir de full
      if (prev[componentId] === 'full' && state !== 'full' && previousStatesRef.current) {
        const restored = { ...previousStatesRef.current, [componentId]: state }
        previousStatesRef.current = null
        return restored
      }
      return { ...prev, [componentId]: state }
    })
  }, [])

  const expandComponent = useCallback((id) => {
    setComponentStates(prev => {
      const current = prev[id] || 'compact'
      const next = NEXT_STATE[current] || current
      if (next === 'full') {
        previousStatesRef.current = { ...prev }
        const all = {}
        for (const k of Object.keys(prev)) all[k] = k === id ? 'full' : 'micro'
        return all
      }
      return { ...prev, [id]: next }
    })
  }, [])

  const collapseComponent = useCallback((id) => {
    setComponentStates(prev => {
      const current = prev[id] || 'compact'
      return { ...prev, [id]: PREV_STATE[current] || current }
    })
  }, [])

  const isFullscreen = useMemo(
    () => Object.values(componentStates).some(s => s === 'full'),
    [componentStates]
  )

  const value = useMemo(() => ({
    componentStates,
    setComponentState,
    expandComponent,
    collapseComponent,
    isFullscreen,
    currentContext,
    setCurrentContext
  }), [componentStates, setComponentState, expandComponent, collapseComponent, isFullscreen, currentContext])

  return (
    <FluidContext.Provider value={value}>
      <LayoutGroup>
        {children}
      </LayoutGroup>
    </FluidContext.Provider>
  )
}

export function useFluid() {
  const ctx = useContext(FluidContext)
  if (!ctx) throw new Error('useFluid must be used within FluidProvider')
  return ctx
}
